import React, { useState, useEffect, useRef, useId } from "react";
import { Link, useNavigate } from "react-router-dom";
import { addMonths } from "date-fns";
import { useHotel } from "../../contexts/HotelContext";
import {
  useKiedyNaboryCustomersQuery,
  useMonthlySettlementQuery,
  useCreateOperationMutation,
} from "../../hooks/useKiedyNaborySettlements";
import {
  useAdminSubscriptionDetailsQuery,
  useAdminSubscriptionHistoryQuery,
  useChangeSubscriptionStatusMutation,
  useCreateManualSubscriptionMutation,
  useRegisterBankTransferPaymentMutation,
  useCreateBillingOperationMutation,
  useUpdateBillingPeriodMutation,
  useSwitchToBankTransferMutation,
  useCreateAdminProformaMutation,
} from "../../hooks/useAdminSubscriptions";
import { useSubscriptionPlans } from "../../hooks/useSubscriptionPlans";
import Layout from "../../components/Layout";
import UnitEconomicsTab from "../../components/admin/unit-economics/UnitEconomicsTab";
import MetricBreakdownPopover from "../../components/admin/unit-economics/MetricBreakdownPopover";
import {
  MonthlySettlement,
  CreateOperationDto,
  SettlementOperationType,
  ProjectedPayment,
  ForecastChurn,
  ForecastProformaSummary,
  KiedyNaboryCustomer,
} from "../../types/kiedynaborySettlements";
import {
  calculateForecastInflow,
  clampChurnPct,
  describeChurnState,
  describeOpenProformas,
  ForecastInflowResult,
  validateManualChurnPct,
} from "../../utils/settlementForecastMath";
import {
  SubscriptionStatus,
  BillingPeriod,
  CreateBillingOperationDto,
  BillingOperationPaymentMethod,
  AdminProformaBillingInterval,
  SubscriptionDetails,
} from "../../types/adminSubscriptions";
import { SubscriptionPrice } from "../../services/accountService";
import { isNotFoundError } from "../../utils/errorUtils";
import { useToast } from "../../contexts/ToastContext";
import {
  BillingSourceKind,
  BillingSourceFilter,
  LEGACY_BILLING_SOURCE_VALUES,
  formatBillingSource,
} from "../../utils/formatBillingSource";

// Helper: format grosze to PLN
// Uses ISO 4217 code display ("PLN") per .claude/rules/frontend/currency-formatting.md.
const formatPLN = (grosze: number): string => {
  return (grosze / 100).toLocaleString("pl-PL", {
    style: "currency",
    currency: "PLN",
    currencyDisplay: "code",
    maximumFractionDigits: 0,
  });
};

// Helper: get current month in YYYY-MM format
const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// Helper: format month for display
const formatMonth = (month: string): string => {
  const date = new Date(month + "-01");
  return date.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "long",
  });
};

const formatDate = (value?: string | null): string => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("pl-PL");
};

// Format a date-only string (YYYY-MM-DD, e.g. proforma due dates / churn window
// bounds — AC-6/AC-9) as DD.MM.YYYY without going through `Date`, which would
// interpret it as UTC midnight and can shift a day depending on the viewer's
// timezone. Plain string slicing keeps it timezone-safe.
const formatIsoDatePL = (isoDate: string): string => {
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

// Format a churn percentage (0-100, one decimal step — AC-7) as e.g. "18,5%".
// Churn % without the "%" sign (input placeholder).
const formatChurnNumber = (pct: number): string =>
  pct.toLocaleString("pl-PL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const formatChurnPct = (pct: number): string => `${formatChurnNumber(pct)}%`;

// Business timezone — the backend computes "po terminie" in the same zone.
const WARSAW_TIME_ZONE = "Europe/Warsaw";

// True when `month` (YYYY-MM) is before the current calendar month in Europe/Warsaw.
// WHY on the frontend: "month ended" only changes presentation (informational,
// muted tile), not the math, so it is derived from the viewer's clock instead of
// widening the API contract. Limitation: a skewed client clock can misclassify the
// month around the boundary (see spec risks).
const isMonthEnded = (month: string): boolean => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: WARSAW_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((p) => p.type === "year")?.value;
  const currentMonth = parts.find((p) => p.type === "month")?.value;
  return month < `${year}-${currentMonth}`;
};

const toDateInputValue = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toIsoEndOfDay = (value: string): string => {
  const date = new Date(`${value}T23:59:59`);
  return date.toISOString();
};

const toIsoStartOfDay = (value: string): string => {
  const date = new Date(`${value}T00:00:00`);
  return date.toISOString();
};

// Tab type
type TabType = "customers" | "settlements" | "unit-economics";

const KiedyNaborySettlements: React.FC = () => {
  const { user } = useHotel();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("customers");
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);

  const isAdmin = user?.role === "admin";
  const hasModuleAccess =
    isAdmin || user?.allEnabledModules?.includes("admin:kiedynabory");

  // Redirect unauthorized users
  if (!hasModuleAccess) {
    navigate("/");
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Panel rozliczeń KiedyNabory.pl
          </h1>
          <p className="text-neutral-600 mt-1">
            Przeglądaj klientów i rozliczenia z Pauliną
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("customers")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "customers"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-neutral-500 hover:text-neutral-700"
              }`}
            >
              Klienci KiedyNabory.pl
            </button>
            <button
              onClick={() => setActiveTab("settlements")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "settlements"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-neutral-500 hover:text-neutral-700"
              }`}
            >
              Rozliczenia miesięczne
            </button>
            <button
              onClick={() => setActiveTab("unit-economics")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "unit-economics"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-neutral-500 hover:text-neutral-700"
              }`}
            >
              Unit economics
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "customers" && <CustomersTab />}
        {activeTab === "settlements" && (
          <SettlementsTab
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            isAdmin={isAdmin}
            onAddOperation={() => setShowAddModal(true)}
            onAddBankTransfer={() => setShowBankTransferModal(true)}
          />
        )}
        {activeTab === "unit-economics" && (
          <UnitEconomicsTab canWrite={isAdmin} />
        )}

        {/* Add Operation Modal */}
        {showAddModal && (
          <AddOperationModal
            month={selectedMonth}
            onClose={() => setShowAddModal(false)}
          />
        )}
        {showBankTransferModal && (
          <BankTransferPaymentModal
            onClose={() => setShowBankTransferModal(false)}
          />
        )}
      </div>
    </Layout>
  );
};

// Helper: get validity date from customer
const getValidityDate = (customer: KiedyNaboryCustomer): Date | null => {
  const trialEnd = customer.trialEndsAt ? new Date(customer.trialEndsAt) : null;
  const periodEnd = customer.currentPeriodEnd ? new Date(customer.currentPeriodEnd) : null;
  if (trialEnd && periodEnd) {
    return periodEnd > trialEnd ? periodEnd : trialEnd;
  }
  return periodEnd || trialEnd;
};

// Status filter options
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Wszystkie statusy" },
  { value: "active", label: "Aktywna" },
  { value: "trialing", label: "Trial" },
  { value: "past_due", label: "Zaległa" },
  { value: "canceled", label: "Anulowana" },
  { value: "unpaid", label: "Niezapłacona" },
  { value: "suspended", label: "Zawieszona" },
  { value: "incomplete", label: "Niekompletna" },
  { value: "none", label: "Brak" },
];

type SortDirection = "asc" | "desc" | null;

// Billing period filter options
const BILLING_PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Wszystkie okresy" },
  { value: "monthly", label: "Miesiąc" },
  { value: "quarterly", label: "Kwartał" },
  { value: "yearly", label: "Rok" },
  { value: "none", label: "Brak" },
];

// Billing source filter options
// "legacy" groups legacy_bank_transfer + legacy_unknown (1 UI option, 2 enum values — AC-5)
const BILLING_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Wszystkie" },
  { value: "stripe", label: "Stripe" },
  { value: "legacy", label: "Stary billing" },
  { value: "billing_bc", label: "Nowy billing" },
];

// Customers Tab Component
const CustomersTab: React.FC = () => {
  const { data: customers, isLoading, error } = useKiedyNaboryCustomersQuery();
  const [selectedCustomer, setSelectedCustomer] =
    useState<KiedyNaboryCustomer | null>(null);
  const [proformaCustomer, setProformaCustomer] =
    useState<KiedyNaboryCustomer | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [billingPeriodFilter, setBillingPeriodFilter] = useState<string>("");
  const [billingSourceFilter, setBillingSourceFilter] = useState<BillingSourceFilter>("");
  const [validitySortDirection, setValiditySortDirection] = useState<SortDirection>("asc");

  // Ref for focus management - stores the button that opened the proforma modal
  const proformaTriggerRef = useRef<HTMLButtonElement | null>(null);
  // Ref for focus management - stores the row that opened the subscription details modal
  const subscriptionDetailsTriggerRef = useRef<HTMLElement | null>(null);

  // Filter and sort customers
  const filteredAndSortedCustomers = React.useMemo(() => {
    if (!customers) return [];

    let result = [...customers];

    // Filter by status
    if (statusFilter) {
      result = result.filter((c) => c.subscriptionStatus === statusFilter);
    }

    // Filter by billing period
    if (billingPeriodFilter) {
      if (billingPeriodFilter === "none") {
        result = result.filter((c) => !c.billingPeriod);
      } else {
        result = result.filter((c) => c.billingPeriod === billingPeriodFilter);
      }
    }

    // Filter by billing source
    // "legacy" groups legacy_bank_transfer + legacy_unknown (AC-5)
    if (billingSourceFilter) {
      if (billingSourceFilter === "legacy") {
        result = result.filter((c) =>
          (LEGACY_BILLING_SOURCE_VALUES as readonly string[]).includes(c.billingSource ?? "")
        );
      } else {
        result = result.filter((c) => c.billingSource === billingSourceFilter);
      }
    }

    // Sort by validity date
    if (validitySortDirection) {
      result.sort((a, b) => {
        const dateA = getValidityDate(a);
        const dateB = getValidityDate(b);

        // Handle null values - nulls go to the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const comparison = dateA.getTime() - dateB.getTime();
        return validitySortDirection === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [customers, statusFilter, billingPeriodFilter, billingSourceFilter, validitySortDirection]);

  const toggleValiditySort = () => {
    setValiditySortDirection((prev) => {
      if (prev === null) return "asc";
      if (prev === "asc") return "desc";
      return "asc";
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">Błąd ładowania klientów</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            Lista klientów ({filteredAndSortedCustomers.length}
            {(statusFilter || billingPeriodFilter || billingSourceFilter) && customers
              ? ` z ${customers.length}`
              : ""})
          </h2>
          <div className="flex items-center gap-4">
            <label htmlFor="statusFilter" className="text-sm text-neutral-600">Status:</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-neutral-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label htmlFor="billingPeriodFilter" className="text-sm text-neutral-600">Okres:</label>
            <select
              id="billingPeriodFilter"
              value={billingPeriodFilter}
              onChange={(e) => setBillingPeriodFilter(e.target.value)}
              className="px-3 py-1.5 border border-neutral-300 rounded-md text-sm"
            >
              {BILLING_PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label htmlFor="billingSourceFilter" className="text-sm text-neutral-600">Billing:</label>
            <select
              id="billingSourceFilter"
              value={billingSourceFilter}
              onChange={(e) => setBillingSourceFilter(e.target.value as BillingSourceFilter)}
              className="px-3 py-1.5 border border-neutral-300 rounded-md text-sm"
            >
              {BILLING_SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  NIP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Okres
                </th>
                <th
                  role="columnheader"
                  aria-sort={
                    validitySortDirection === "asc"
                      ? "ascending"
                      : validitySortDirection === "desc"
                      ? "descending"
                      : "none"
                  }
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={toggleValiditySort}
                >
                  <span className="flex items-center gap-1">
                    Ważność
                    {validitySortDirection === "asc" && (
                      <span aria-hidden="true" className="text-primary-600">↑</span>
                    )}
                    {validitySortDirection === "desc" && (
                      <span aria-hidden="true" className="text-primary-600">↓</span>
                    )}
                  </span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredAndSortedCustomers.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-8 text-center text-neutral-500"
                  >
                    {(statusFilter || billingPeriodFilter || billingSourceFilter)
                      ? "Brak klientów spełniających kryteria filtrowania"
                      : "Brak klientów KiedyNabory.pl"}
                  </td>
                </tr>
              )}
              {filteredAndSortedCustomers.map((customer) => {
                const validityDate = getValidityDate(customer);
                return (
                  <tr
                    key={customer.tenantId}
                    tabIndex={0}
                    className="hover:bg-neutral-50 cursor-pointer"
                    onClick={(e) => {
                      subscriptionDetailsTriggerRef.current = e.currentTarget;
                      setSelectedCustomer(customer);
                    }}
                  >
                    <td className="px-6 py-4 text-left text-sm font-medium text-neutral-900">
                      {customer.tenantName}
                    </td>
                    <td className="px-6 py-4 text-left text-sm text-neutral-600">
                      {customer.email || "-"}
                    </td>
                    <td className="px-6 py-4 text-left text-sm text-neutral-600 font-mono">
                      {customer.nip || "-"}
                    </td>
                    <td className="px-6 py-4 text-left text-sm">
                      {(() => {
                        const billingSource = customer.billingSource ?? null;
                        const { label, className } = formatBillingSource(billingSource);
                        if (billingSource === null) {
                          // null: neutral span, NOT a badge (spec: AC-4)
                          return <span className={className}>{label}</span>;
                        }
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>
                            {label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-left">
                      <SubscriptionStatusBadge
                        status={customer.subscriptionStatus}
                      />
                    </td>
                    <td className="px-6 py-4 text-left text-sm text-neutral-600">
                      <BillingPeriodBadge period={customer.billingPeriod} />
                    </td>
                    <td className="px-6 py-4 text-left text-sm text-neutral-600">
                      {validityDate ? validityDate.toLocaleDateString("pl-PL") : "-"}
                    </td>
                    <td className="px-6 py-4 text-left">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          proformaTriggerRef.current = e.currentTarget;
                          setProformaCustomer(customer);
                        }}
                        aria-label={`Wystaw proforme dla ${customer.tenantName}`}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Wystaw proforme
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {selectedCustomer && (
        <SubscriptionDetailsModal
          customer={selectedCustomer}
          onClose={() => {
            setSelectedCustomer(null);
            // Restore focus to the row that opened the modal
            subscriptionDetailsTriggerRef.current?.focus();
          }}
        />
      )}
      {proformaCustomer && (
        <ProformaModal
          customer={proformaCustomer}
          onClose={() => {
            setProformaCustomer(null);
            // Restore focus to the button that opened the modal
            proformaTriggerRef.current?.focus();
          }}
        />
      )}
    </>
  );
};

/**
 * Modal lokalnie wyprowadza billingSource z `subscription + subscriptionSource`
 * zamiast używać `customer.billingSource` z DTO.
 *
 * Powód (ADR-068 D3 exception): modal otrzymuje subscription z innego
 * endpointu/cache niż lista, więc poleganie na DTO mogłoby pokazywać stale
 * dane gdy subskrypcja zmieniła się między listą a otwarciem modala.
 * Klasyfikacja w modalu replikuje ten sam algorytm co backend
 * (deriveBillingSourceFromPaymentSource w UnifiedSubscriptionView mapper).
 */
function deriveModalBillingSource(
  subscription: SubscriptionDetails | null | undefined,
  subscriptionSource: "legacy" | "billing" | null
): BillingSourceKind | null {
  if (!subscription) return null;
  if (subscriptionSource === "billing") return "billing_bc";
  if (subscription.paymentSource === "stripe") return "stripe";
  if (subscription.paymentSource === "bank_transfer") return "legacy_bank_transfer";
  return "legacy_unknown";
}

const SubscriptionDetailsModal: React.FC<{
  customer: KiedyNaboryCustomer;
  onClose: () => void;
}> = ({ customer, onClose }) => {
  const tenantId = customer.tenantId;
  const { addToast } = useToast();
  const detailsQuery = useAdminSubscriptionDetailsQuery(tenantId);
  const [historyLimit, setHistoryLimit] = useState(10);
  const historyQuery = useAdminSubscriptionHistoryQuery(
    tenantId,
    historyLimit,
    0
  );
  const changeStatusMutation = useChangeSubscriptionStatusMutation(tenantId);
  const createManualMutation = useCreateManualSubscriptionMutation(tenantId);
  const updateBillingPeriodMutation = useUpdateBillingPeriodMutation(tenantId);
  const switchToBankTransferMutation = useSwitchToBankTransferMutation(tenantId);
  const { data: plans } = useSubscriptionPlans();
  const [showBillingOperationModal, setShowBillingOperationModal] = useState(false);

  // Billing period inline edit state
  const [editBillingPeriod, setEditBillingPeriod] = useState<"monthly" | "quarterly" | "yearly" | "">(
    customer.billingPeriod || ""
  );
  useEffect(() => {
    setEditBillingPeriod(customer.billingPeriod || "");
  }, [customer.billingPeriod]);
  const [billingPeriodFeedback, setBillingPeriodFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Stripe -> bank transfer switch (unblocks billing period + migration to Billing BC)
  const [showSwitchToBankTransfer, setShowSwitchToBankTransfer] = useState(false);
  const [switchReason, setSwitchReason] = useState("");
  const [switchError, setSwitchError] = useState<string | null>(null);

  const subscription = detailsQuery.data?.subscription;
  const subscriptionSource = detailsQuery.data?.source ?? null;
  const tenantHasMigratedLegacy =
    detailsQuery.data?.tenantHasMigratedLegacy ?? false;
  const viewKey = detailsQuery.data?.viewKey ?? null;
  const isBillingSource = subscriptionSource === 'billing';
  const billingTooltipText =
    'Subskrypcja zarządzana w Billing BC — użyj /admin/billing/subscriptions';
  const billingTooltipId = `billing-managed-tooltip-${tenantId}`;
  // Scenariusz 6: brak subskrypcji w obu schematach (subscription === null AND not migrated)
  const showManualCreationForm =
    !!detailsQuery.data && !subscription && !tenantHasMigratedLegacy;
  // Scenariusz 3: tenant zmigrowany z legacy, ale brak rekordu w Billing BC (broken state)
  const showBrokenMigrationBanner =
    !!detailsQuery.data && !subscription && tenantHasMigratedLegacy;
  const isNotFound = isNotFoundError(detailsQuery.error);

  const [statusForm, setStatusForm] = useState<{
    status: SubscriptionStatus;
    reason: string;
    periodEnd: string;
    sendEmail: boolean;
    includeReasonInEmail: boolean;
  }>({
    status: "active",
    reason: "",
    periodEnd: "",
    sendEmail: true,
    includeReasonInEmail: false,
  });

  const [manualForm, setManualForm] = useState({
    planId: "",
    status: "active" as "active" | "trialing",
    periodStart: toDateInputValue(new Date().toISOString()),
    periodEnd: toDateInputValue(addMonths(new Date(), 1).toISOString()),
    billingPeriod: "monthly" as "monthly" | "quarterly" | "yearly",
    adminNotes: "",
  });

  useEffect(() => {
    if (!subscription) return;
    setStatusForm((prev) => ({
      ...prev,
      status: subscription.status,
      periodEnd: toDateInputValue(subscription.currentPeriodEnd),
    }));
  }, [subscription]);

  useEffect(() => {
    if (!plans?.length || manualForm.planId) return;
    setManualForm((prev) => ({ ...prev, planId: plans[0].id }));
  }, [plans, manualForm.planId]);

  const requiresPeriodEnd = statusForm.status === "active";
  const canSubmitStatus =
    statusForm.reason.trim().length >= 10 &&
    (!requiresPeriodEnd || !!statusForm.periodEnd);

  const handleStatusSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmitStatus) return;

    changeStatusMutation.mutate({
      status: statusForm.status,
      reason: statusForm.reason.trim(),
      periodEnd: statusForm.periodEnd
        ? toIsoEndOfDay(statusForm.periodEnd)
        : undefined,
      sendEmail: statusForm.sendEmail,
      includeReasonInEmail: statusForm.includeReasonInEmail,
    });
  };

  const handleManualSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!manualForm.planId) return;

    createManualMutation.mutate({
      planId: manualForm.planId,
      status: manualForm.status,
      periodStart: toIsoStartOfDay(manualForm.periodStart),
      periodEnd: toIsoEndOfDay(manualForm.periodEnd),
      billingPeriod: manualForm.billingPeriod,
      adminNotes: manualForm.adminNotes || undefined,
    });
  };

  const applyQuickPeriod = (months: number) => {
    const nextDate = addMonths(new Date(), months);
    setStatusForm((prev) => ({
      ...prev,
      periodEnd: toDateInputValue(nextDate.toISOString()),
    }));
  };

  // AC-6: Derive billingSource locally from source + paymentSource for modal "Źródło" field.
  // Uses formatBillingSource() as SSOT — consistent with list column labels.
  const modalBillingSource = deriveModalBillingSource(subscription, subscriptionSource);
  const modalBillingSourceFormatted = formatBillingSource(modalBillingSource);

  const isStripeSubscription = subscription?.paymentSource === "stripe";

  const handleSwitchToBankTransfer = () => {
    if (switchReason.trim().length < 10) return;
    setSwitchError(null);

    switchToBankTransferMutation.mutate(
      { reason: switchReason.trim() },
      {
        onSuccess: () => {
          setShowSwitchToBankTransfer(false);
          setSwitchReason("");
        },
        onError: (error: any) => {
          setSwitchError(
            error.response?.data?.message ||
              "Nie udało się przełączyć subskrypcji na przelew"
          );
        },
      }
    );
  };

  const handleBillingPeriodSave = () => {
    if (!editBillingPeriod) return;
    setBillingPeriodFeedback(null);

    updateBillingPeriodMutation.mutate(
      { billingPeriod: editBillingPeriod as "monthly" | "quarterly" | "yearly" },
      {
        onSuccess: () => {
          setBillingPeriodFeedback({
            type: "success",
            message: "Okres rozliczeniowy zaktualizowany",
          });
          setTimeout(() => setBillingPeriodFeedback(null), 3000);
        },
        onError: (error: any) => {
          setBillingPeriodFeedback({
            type: "error",
            message:
              error.response?.data?.message ||
              "Nie udało się zaktualizować okresu rozliczeniowego",
          });
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {customer.tenantName}
            </h3>
            <p className="text-sm text-neutral-500">
              {customer.tenantId} •{" "}
              {subscription?.subdomain || "brak subdomeny"} •{" "}
              {customer.nip || "Brak NIP"}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Zamknij modal"
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {detailsQuery.isLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          )}

          {!detailsQuery.isLoading && showBrokenMigrationBanner && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="flex items-start text-red-700 text-sm">
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  Tenant zmigrowany, brak aktywnej subskrypcji w Billing BC —
                  nie twórz nowej legacy, skontaktuj się z developmentem
                </span>
              </p>
            </div>
          )}

          {!detailsQuery.isLoading &&
            (isNotFound || showManualCreationForm) && (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <p className="text-amber-700 text-sm">
                  Brak aktywnej subskrypcji dla tego klienta. Możesz utworzyć
                  subskrypcję ręcznie.
                </p>
              </div>

              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Plan subskrypcji
                    </label>
                    <select
                      value={manualForm.planId}
                      onChange={(e) =>
                        setManualForm((prev) => ({
                          ...prev,
                          planId: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                    >
                      <option value="">Wybierz plan</option>
                      {plans?.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Status początkowy
                    </label>
                    <select
                      value={manualForm.status}
                      onChange={(e) =>
                        setManualForm((prev) => ({
                          ...prev,
                          status: e.target.value as "active" | "trialing",
                        }))
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                    >
                      <option value="active">Aktywna</option>
                      <option value="trialing">Trial</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Data startu
                    </label>
                    <input
                      type="date"
                      value={manualForm.periodStart}
                      onChange={(e) =>
                        setManualForm((prev) => ({
                          ...prev,
                          periodStart: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Data końca
                    </label>
                    <input
                      type="date"
                      value={manualForm.periodEnd}
                      min={toDateInputValue(new Date().toISOString())}
                      onChange={(e) =>
                        setManualForm((prev) => ({
                          ...prev,
                          periodEnd: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Okres rozliczeniowy<span className="text-red-500"> *</span>
                  </label>
                  <select
                    value={manualForm.billingPeriod}
                    onChange={(e) =>
                      setManualForm((prev) => ({
                        ...prev,
                        billingPeriod: e.target.value as "monthly" | "quarterly" | "yearly",
                      }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  >
                    <option value="monthly">Miesięczny</option>
                    <option value="quarterly">Kwartalny</option>
                    <option value="yearly">Roczny</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Notatki admina
                  </label>
                  <textarea
                    value={manualForm.adminNotes}
                    onChange={(e) =>
                      setManualForm((prev) => ({
                        ...prev,
                        adminNotes: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                    placeholder="Opcjonalne notatki"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={
                      createManualMutation.isPending ||
                      !manualForm.planId ||
                      !manualForm.periodEnd ||
                      !manualForm.billingPeriod
                    }
                    className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-4 py-2 rounded-md"
                  >
                    {createManualMutation.isPending
                      ? "Zapisywanie..."
                      : "Utwórz subskrypcję"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {!detailsQuery.isLoading && detailsQuery.error && !isNotFound && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700 text-sm">
              Nie udało się załadować danych subskrypcji.
            </div>
          )}

          {!detailsQuery.isLoading && subscription && (
            <div key={viewKey ?? `${tenantId}-fallback`}>
              {isBillingSource && (
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                    Nowy system billing
                  </span>
                  <Link
                    to={{
                      pathname: "/admin/billing/subscriptions",
                      search: `?tenantId=${tenantId}`,
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 underline"
                  >
                    Zarządzaj w Billing BC
                  </Link>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4">
                  <div className="text-xs uppercase text-neutral-500 mb-1">
                    Status
                  </div>
                  <SubscriptionStatusBadge status={subscription.status} />
                  <div className="text-xs text-neutral-500 mt-2">
                    Źródło:{" "}
                    {modalBillingSource === null ? (
                      <span className={modalBillingSourceFormatted.className}>
                        {modalBillingSourceFormatted.label}
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${modalBillingSourceFormatted.className}`}>
                        {modalBillingSourceFormatted.label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4">
                  <div className="text-xs uppercase text-neutral-500 mb-1">
                    Plan
                  </div>
                  <div className="text-sm font-medium text-neutral-900">
                    {subscription.planName}
                  </div>
                  <div className="text-xs text-neutral-500 mt-2">
                    Okres: {formatDate(subscription.currentPeriodStart)} →{" "}
                    {formatDate(subscription.currentPeriodEnd)}
                  </div>
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <div
                      id={`modal-billing-period-label-${tenantId}`}
                      className="text-xs uppercase text-neutral-500 mb-1"
                    >
                      Okres rozliczeniowy
                    </div>
                    {isStripeSubscription ? (
                      <div>
                        <select
                          disabled
                          aria-labelledby={`modal-billing-period-label-${tenantId}`}
                          aria-describedby={`stripe-managed-tooltip-${tenantId}`}
                          value={customer.billingPeriod || ""}
                          className="w-full px-2 py-1 text-xs border border-neutral-200 rounded bg-neutral-100 text-neutral-400 cursor-not-allowed"
                        >
                          {customer.billingPeriod ? (
                            <option value={customer.billingPeriod}>
                              {customer.billingPeriod === "monthly" ? "Miesięczny" : customer.billingPeriod === "quarterly" ? "Kwartalny" : "Roczny"}
                            </option>
                          ) : (
                            <option value="">Nie ustawiony</option>
                          )}
                        </select>
                        <div
                          id={`stripe-managed-tooltip-${tenantId}`}
                          role="note"
                          className="mt-1 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-md p-2"
                        >
                          Zarządzane przez Stripe
                        </div>
                        {/*
                          Stripe subscriptions carry billing_period=NULL, and the dropdown
                          above refuses them — which blocks migration to Billing BC entirely.
                          This is the way out. Spec 2026-09-02-switch-stripe-subscription-to-bank-transfer.
                        */}
                        {!showSwitchToBankTransfer ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSwitchError(null);
                              setShowSwitchToBankTransfer(true);
                            }}
                            className="mt-2 w-full px-2 py-1 text-xs border border-primary-300 text-primary-700 hover:bg-primary-50 rounded"
                          >
                            Przełącz na przelew
                          </button>
                        ) : (
                          <div className="mt-2 border border-amber-300 bg-amber-50 rounded-md p-2">
                            <div
                              role="note"
                              className="text-xs text-amber-900 mb-2"
                            >
                              <strong>Najpierw anuluj subskrypcję w Stripe.</strong>{" "}
                              Jeśli karta nadal będzie obciążana, klient zapłaci
                              dwa razy — kartą i przelewem.
                            </div>
                            <label
                              htmlFor={`switch-reason-${tenantId}`}
                              className="block text-xs text-neutral-700 mb-1"
                            >
                              Powód (min. 10 znaków)
                            </label>
                            <textarea
                              id={`switch-reason-${tenantId}`}
                              value={switchReason}
                              onChange={(e) => setSwitchReason(e.target.value)}
                              rows={2}
                              disabled={switchToBankTransferMutation.isPending}
                              className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                              placeholder="np. Klient anulował kartę, przechodzi na przelew przed migracją billingu"
                            />
                            <div className="flex items-center gap-1 mt-2">
                              <button
                                type="button"
                                onClick={handleSwitchToBankTransfer}
                                disabled={
                                  switchToBankTransferMutation.isPending ||
                                  switchReason.trim().length < 10
                                }
                                className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white rounded"
                              >
                                {switchToBankTransferMutation.isPending
                                  ? "Przełączanie..."
                                  : "Potwierdź"}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowSwitchToBankTransfer(false);
                                  setSwitchReason("");
                                  setSwitchError(null);
                                }}
                                disabled={switchToBankTransferMutation.isPending}
                                className="px-2 py-1 text-xs border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded"
                              >
                                Anuluj
                              </button>
                            </div>
                            {switchError && (
                              <div
                                role="status"
                                aria-live="polite"
                                className="mt-1 text-xs text-red-600"
                              >
                                {switchError}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <select
                          value={editBillingPeriod}
                          onChange={(e) =>
                            setEditBillingPeriod(e.target.value as "monthly" | "quarterly" | "yearly" | "")
                          }
                          disabled={updateBillingPeriodMutation.isPending}
                          className="flex-1 px-2 py-1 text-xs border border-neutral-300 rounded"
                        >
                          {!customer.billingPeriod && !editBillingPeriod && (
                            <option value="" disabled>Nie ustawiony</option>
                          )}
                          <option value="monthly">Miesięczny</option>
                          <option value="quarterly">Kwartalny</option>
                          <option value="yearly">Roczny</option>
                        </select>
                        <button
                          type="button"
                          onClick={handleBillingPeriodSave}
                          disabled={
                            updateBillingPeriodMutation.isPending ||
                            !editBillingPeriod ||
                            editBillingPeriod === customer.billingPeriod
                          }
                          className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white rounded"
                        >
                          {updateBillingPeriodMutation.isPending ? "Zapisywanie..." : "Zapisz"}
                        </button>
                      </div>
                    )}
                    {billingPeriodFeedback && (
                      <div
                        role="status"
                        aria-live="polite"
                        className={`mt-1 text-xs ${
                          billingPeriodFeedback.type === "success"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {billingPeriodFeedback.message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4">
                  <div className="text-xs uppercase text-neutral-500 mb-1">
                    Trial
                  </div>
                  <div className="text-sm text-neutral-700">
                    {formatDate(subscription.trialEndsAt)}
                  </div>
                  {/* M1: manuallyManaged jest null dla source='billing' — ukrywamy bo nie dotyczy. */}
                  {subscription.manuallyManaged !== null && (
                    <div className="text-xs text-neutral-500 mt-2">
                      Manual: {subscription.manuallyManaged ? "tak" : "nie"}
                    </div>
                  )}
                </div>
              </div>

              {subscription.adminNotes && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4 text-sm text-neutral-700">
                  <div className="text-xs uppercase text-neutral-500 mb-1">
                    Notatki admina
                  </div>
                  {subscription.adminNotes}
                </div>
              )}

              {/* Billing Operation Button */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowBillingOperationModal(true)}
                  className="bg-primary-700 hover:bg-primary-800 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Nowa operacja billingowa
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="text-sm font-semibold text-neutral-900">
                    Zmień status subskrypcji
                  </div>
                  {isBillingSource && (
                    <div
                      id={billingTooltipId}
                      role="note"
                      className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-md p-2"
                    >
                      {billingTooltipText}
                    </div>
                  )}
                  <form
                    onSubmit={(e) => {
                      if (isBillingSource) {
                        e.preventDefault();
                        return;
                      }
                      handleStatusSubmit(e);
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Nowy status
                      </label>
                      <select
                        value={statusForm.status}
                        onChange={(e) =>
                          setStatusForm((prev) => ({
                            ...prev,
                            status: e.target.value as SubscriptionStatus,
                          }))
                        }
                        aria-disabled={isBillingSource ? 'true' : undefined}
                        aria-describedby={isBillingSource ? billingTooltipId : undefined}
                        tabIndex={isBillingSource ? 0 : undefined}
                        className={`w-full px-3 py-2 border border-neutral-300 rounded-md ${
                          isBillingSource ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed' : ''
                        }`}
                        onMouseDown={isBillingSource ? (e) => e.preventDefault() : undefined}
                        onKeyDown={isBillingSource ? (e) => e.preventDefault() : undefined}
                      >
                        <option value="active">Aktywna</option>
                        <option value="trialing">Trial</option>
                        <option value="past_due">Zaległa</option>
                        <option value="canceled">Anulowana</option>
                        <option value="suspended">Zawieszona</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Powód (min. 10 znaków, dla auditu)
                      </label>
                      <textarea
                        value={statusForm.reason}
                        onChange={(e) =>
                          setStatusForm((prev) => ({
                            ...prev,
                            reason: e.target.value,
                          }))
                        }
                        rows={3}
                        placeholder="Wymagany dla auditu"
                        disabled={isBillingSource || changeStatusMutation.isPending}
                        aria-describedby={isBillingSource ? billingTooltipId : undefined}
                        className={`w-full px-3 py-2 border border-neutral-300 rounded-md ${
                          isBillingSource ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed' : ''
                        }`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Nowa data ważności
                        {requiresPeriodEnd && (
                          <span className="text-red-500"> *</span>
                        )}
                      </label>
                      <input
                        type="date"
                        value={statusForm.periodEnd}
                        min={toDateInputValue(new Date().toISOString())}
                        onChange={(e) =>
                          setStatusForm((prev) => ({
                            ...prev,
                            periodEnd: e.target.value,
                          }))
                        }
                        disabled={isBillingSource || changeStatusMutation.isPending}
                        aria-describedby={isBillingSource ? billingTooltipId : undefined}
                        className={`w-full px-3 py-2 border border-neutral-300 rounded-md ${
                          isBillingSource ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed' : ''
                        }`}
                      />
                      <div className="flex gap-2 mt-2 text-xs">
                        <button
                          type="button"
                          onClick={isBillingSource ? undefined : () => applyQuickPeriod(1)}
                          aria-disabled={isBillingSource ? 'true' : undefined}
                          aria-describedby={isBillingSource ? billingTooltipId : undefined}
                          tabIndex={isBillingSource ? 0 : undefined}
                          className={`px-2 py-1 border border-neutral-200 rounded-md ${
                            isBillingSource
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-neutral-50'
                          }`}
                        >
                          +1 mies.
                        </button>
                        <button
                          type="button"
                          onClick={isBillingSource ? undefined : () => applyQuickPeriod(3)}
                          aria-disabled={isBillingSource ? 'true' : undefined}
                          aria-describedby={isBillingSource ? billingTooltipId : undefined}
                          tabIndex={isBillingSource ? 0 : undefined}
                          className={`px-2 py-1 border border-neutral-200 rounded-md ${
                            isBillingSource
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-neutral-50'
                          }`}
                        >
                          +3 mies.
                        </button>
                        <button
                          type="button"
                          onClick={isBillingSource ? undefined : () => applyQuickPeriod(12)}
                          aria-disabled={isBillingSource ? 'true' : undefined}
                          aria-describedby={isBillingSource ? billingTooltipId : undefined}
                          tabIndex={isBillingSource ? 0 : undefined}
                          className={`px-2 py-1 border border-neutral-200 rounded-md ${
                            isBillingSource
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-neutral-50'
                          }`}
                        >
                          +12 mies.
                        </button>
                      </div>
                    </div>

                    {/* Email notification options */}
                    <div className="space-y-2 pt-2 border-t border-neutral-200">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="sendEmail"
                          checked={statusForm.sendEmail}
                          onChange={(e) =>
                            setStatusForm((prev) => ({
                              ...prev,
                              sendEmail: e.target.checked,
                              // Disable includeReasonInEmail if sendEmail is unchecked
                              includeReasonInEmail: e.target.checked ? prev.includeReasonInEmail : false,
                            }))
                          }
                          disabled={isBillingSource || changeStatusMutation.isPending}
                          aria-describedby={isBillingSource ? billingTooltipId : undefined}
                          className="w-4 h-4"
                        />
                        <label htmlFor="sendEmail" className="text-sm text-neutral-700">
                          Wyślij email do klienta
                        </label>
                      </div>
                      <div className={`ml-6 ${!statusForm.sendEmail ? 'opacity-50' : ''}`}>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="includeReasonInEmail"
                            checked={statusForm.includeReasonInEmail}
                            onChange={(e) =>
                              setStatusForm((prev) => ({
                                ...prev,
                                includeReasonInEmail: e.target.checked,
                              }))
                            }
                            disabled={isBillingSource || !statusForm.sendEmail || changeStatusMutation.isPending}
                            aria-describedby={isBillingSource ? billingTooltipId : undefined}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-neutral-600">
                            Dołącz powód do emaila
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      {isBillingSource ? (
                        <button
                          type="button"
                          aria-disabled="true"
                          aria-describedby={billingTooltipId}
                          tabIndex={0}
                          className="bg-primary-300 text-white px-4 py-2 rounded-md cursor-not-allowed"
                          onClick={(e) => {
                            e.preventDefault();
                            addToast({
                              type: "info",
                              message:
                                "Subskrypcja zarządzana w Billing BC — użyj linku powyżej",
                            });
                          }}
                        >
                          Zapisz zmiany
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={!canSubmitStatus || changeStatusMutation.isPending}
                          className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-4 py-2 rounded-md"
                        >
                          {changeStatusMutation.isPending
                            ? "Zapisywanie..."
                            : "Zapisz zmiany"}
                        </button>
                      )}
                    </div>
                  </form>
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-neutral-900">
                    Historia zmian
                  </div>
                  <div className="space-y-3">
                    {historyQuery.isLoading && (
                      <div className="text-sm text-neutral-500">
                        Ładowanie historii...
                      </div>
                    )}
                    {historyQuery.data?.items?.length === 0 && (
                      <div className="text-sm text-neutral-500">
                        Brak historii zmian
                      </div>
                    )}
                    {historyQuery.data?.items?.map((item) => (
                      <div
                        key={item.id}
                        className="border border-neutral-200 rounded-md p-3"
                      >
                        <div className="text-xs text-neutral-500">
                          {formatDate(item.createdAt)} •{" "}
                          {item.changedByUserEmail || "system"}
                        </div>
                        <div className="text-sm font-medium text-neutral-900">
                          {item.previousStatus || "none"} → {item.newStatus}
                        </div>
                        <div className="text-xs text-neutral-600 mt-1">
                          {item.reason}
                        </div>
                      </div>
                    ))}
                  </div>
                  {historyQuery.data &&
                    historyQuery.data.total > historyQuery.data.items.length && (
                      <button
                        type="button"
                        onClick={() => setHistoryLimit((prev) => prev + 20)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                        disabled={historyQuery.isFetching}
                      >
                        {historyQuery.isFetching
                          ? "Ładowanie..."
                          : "Pokaż więcej"}
                      </button>
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showBillingOperationModal && (
        <BillingOperationModal
          tenantId={tenantId}
          customer={customer}
          currentPeriodEnd={subscription?.currentPeriodEnd}
          onClose={() => setShowBillingOperationModal(false)}
        />
      )}
    </div>
  );
};

// Manual churn correction (AC-7) — localStorage, scoped per selected month.
// Read/write are wrapped in try/catch: any storage failure (disabled storage,
// quota, private mode) is treated as "no override" — the historical rate stays
// in effect, exactly as if nothing had been saved.
const churnOverrideStorageKey = (month: string): string =>
  `kn-settlements-churn-override:${month}`;

const readManualChurnOverride = (month: string): number | null => {
  try {
    const raw = window.localStorage.getItem(churnOverrideStorageKey(month));
    // Number("") / Number("  ") is 0 — an empty value is "no override", not 0%.
    if (raw === null || raw.trim() === "") return null;
    const parsed = Number(raw);
    // Clamp: a hand-edited storage value must not push the forecast out of range.
    return Number.isFinite(parsed) ? clampChurnPct(parsed) : null;
  } catch {
    return null;
  }
};

const writeManualChurnOverride = (month: string, pct: number): boolean => {
  try {
    window.localStorage.setItem(churnOverrideStorageKey(month), String(pct));
    return true;
  } catch {
    return false;
  }
};

const clearManualChurnOverride = (month: string): void => {
  try {
    window.localStorage.removeItem(churnOverrideStorageKey(month));
  } catch {
    // Nothing to do — there's no override to fall back to anyway.
  }
};

/**
 * Manual churn % correction state for the selected month (AC-7).
 * Resets `isEditing`/`draft`/`error` whenever `month` changes, since the
 * correction never carries over between months.
 */
const useManualChurnOverride = (month: string) => {
  const [manualPct, setManualPct] = useState<number | null>(() =>
    readManualChurnOverride(month)
  );
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setManualPct(readManualChurnOverride(month));
    setIsEditing(false);
    setDraft("");
    setError(null);
  }, [month]);

  const startEditing = () => {
    setDraft(manualPct !== null ? String(manualPct) : "");
    setError(null);
    setIsEditing(true);
  };

  const handleDraftChange = (value: string) => {
    setDraft(value);
    const result = validateManualChurnPct(value);
    if (!result.valid) {
      setError(result.error);
      return; // Previous value (if any) stays in effect.
    }
    setError(null);
    const persisted = writeManualChurnOverride(month, result.value);
    // Storage failure = "brak dostępu" (AC-7): historical value stays in effect.
    setManualPct(persisted ? result.value : null);
  };

  const reset = () => {
    clearManualChurnOverride(month);
    setManualPct(null);
    setDraft("");
    setError(null);
  };

  return { manualPct, isEditing, draft, error, startEditing, handleDraftChange, reset };
};

// Settlements Tab Component
const SettlementsTab: React.FC<{
  selectedMonth: string;
  onMonthChange: (month: string) => void;
  isAdmin: boolean;
  onAddOperation: () => void;
  onAddBankTransfer: () => void;
}> = ({
  selectedMonth,
  onMonthChange,
  isAdmin,
  onAddOperation,
  onAddBankTransfer,
}) => {
  const {
    data: settlement,
    isLoading,
    error,
  } = useMonthlySettlementQuery(selectedMonth);

  // Generate month options (last 12 months)
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  });

  return (
    <div className="space-y-6">
      {/* Month Selector & Actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-neutral-700">
            Miesiąc:
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => onMonthChange(e.target.value)}
            className="px-3 py-2 border border-neutral-300 rounded-md text-sm"
          >
            {monthOptions.map((month) => (
              <option key={month} value={month}>
                {formatMonth(month)}
              </option>
            ))}
          </select>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-3">
            <button
              onClick={onAddBankTransfer}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Dodaj płatność przelewową
            </button>
            <button
              onClick={onAddOperation}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Dodaj operację
            </button>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Błąd ładowania rozliczeń</p>
        </div>
      )}

      {settlement && <SettlementContent settlement={settlement} />}
    </div>
  );
};

// Settlement Content Component
const SettlementContent: React.FC<{ settlement: MonthlySettlement }> = ({
  settlement,
}) => {
  const churnOverride = useManualChurnOverride(settlement.month);
  const { proformas } = settlement.forecast;
  // Single computation: tiles and the "Oczekiwane płatności" footer share it.
  const inflow = calculateForecastInflow({
    proformas,
    renewalsTotal: settlement.forecast.renewals.total,
    historicalPct: settlement.forecast.churn.pct,
    manualPct: churnOverride.manualPct,
  });
  const monthlyForecast = settlement.netRevenue + inflow.realisticInflow;

  return (
    <div className="space-y-6">
      {/* Summary Cards - Row 1: Revenue and Costs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Przychód netto"
          value={formatPLN(settlement.netRevenue)}
        />
        <SummaryCard
          label="Prowizje Stripe"
          value={`-${formatPLN(settlement.stripeFees)}`}
          variant="warning"
        />
        <SummaryCard
          label="Koszty stałe"
          value={`-${formatPLN(settlement.fixedCosts)}`}
          variant="warning"
        />
        <SummaryCard
          label="Inne koszty"
          value={
            settlement.otherCosts > 0
              ? `-${formatPLN(settlement.otherCosts)}`
              : formatPLN(0)
          }
          variant={settlement.otherCosts > 0 ? "warning" : "neutral"}
        />
      </div>

      {/* Summary Cards - Forecast row: issued proformas, renewals, churn, and the result (AC-8) */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-2">
          Prognoza wpływów — {formatMonth(settlement.month)}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryCard
            label="Wystawione proformy"
            testId="forecast-tile-proformas"
            value={formatPLN(proformas.total)}
            subtitle={
              <>
                <div>
                  {proformas.count > 0
                    ? describeOpenProformas(proformas.count)
                    : "brak"}
                </div>
                {proformas.overdueCount > 0 && (
                  <div>
                    w tym {proformas.overdueCount} po terminie (
                    {formatPLN(proformas.overdueTotal)})
                  </div>
                )}
              </>
            }
          />
          <SummaryCard
            label="Odnowienia bez proformy"
            value={formatPLN(settlement.forecast.renewals.total)}
            subtitle={
              settlement.forecast.renewals.count > 0
                ? `${settlement.forecast.renewals.count} odnowień`
                : "brak"
            }
          />
          <ChurnForecastCard
            churn={settlement.forecast.churn}
            churnOverride={churnOverride}
            proformas={proformas}
            inflow={inflow}
            monthEnded={isMonthEnded(settlement.month)}
          />
          <SummaryCard
            label="Realnie wpłynie"
            testId="forecast-tile-realistic"
            value={formatPLN(inflow.realisticInflow)}
            subtitle={
              <>
                <div>
                  proformy {formatPLN(inflow.proformaInflow)} + odnowienia{" "}
                  {formatPLN(inflow.renewalInflow)}
                </div>
                {inflow.capBinds && (
                  <div>ograniczone do proform przed terminem</div>
                )}
              </>
            }
          />
          <SummaryCard
            label="Prognoza miesiąca"
            testId="forecast-tile-monthly"
            value={formatPLN(monthlyForecast)}
            variant="success"
          />
        </div>
      </div>

      {/* Summary Cards - Row 2: Split and Balances */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Do podziału"
          value={formatPLN(settlement.toSplit)}
          variant="success"
        />
        <SummaryCard
          label="Udział Michała (50%)"
          value={formatPLN(settlement.ownerShare)}
        />
        <SummaryCard
          label="Udział Pauliny (50%)"
          value={formatPLN(settlement.partnerShare)}
        />
        <SummaryCard
          label="Saldo Pauliny"
          value={formatPLN(settlement.partnerBalance)}
          variant={settlement.partnerBalance > 0 ? "info" : "neutral"}
        />
      </div>

      {/* Projected Payments Section */}
      {settlement.projectedPayments && settlement.projectedPayments.length > 0 && (
        <ProjectedPaymentsSection
          payments={settlement.projectedPayments}
          proformas={proformas}
          inflow={inflow}
        />
      )}

      {/* Operations Table */}
      <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Operacje ({settlement.operations.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Typ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Kwota netto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Prowizja Stripe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Opis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Data
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {settlement.operations.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-neutral-500"
                  >
                    Brak operacji w tym miesiącu
                  </td>
                </tr>
              )}
              {settlement.operations.map((op) => (
                <tr key={op.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <OperationTypeBadge type={op.type} />
                  </td>
                  <td className="px-6 py-4 text-sm font-mono">
                    {formatPLN(op.amount)}
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-neutral-500">
                    {op.stripeFee > 0 ? `-${formatPLN(op.stripeFee)}` : "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {op.description || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {new Date(op.operationDate).toLocaleDateString("pl-PL")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Helper Components
type SummaryCardVariant = "neutral" | "success" | "warning" | "info" | "projected";

const SUMMARY_CARD_VARIANT_STYLES: Record<SummaryCardVariant, string> = {
  neutral: "bg-white border-neutral-200 text-neutral-900",
  success: "bg-green-50 border-green-200 text-green-800",
  warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
  projected: "bg-purple-50 border-purple-200 text-purple-800",
};

const SummaryCard: React.FC<{
  label: string;
  value: React.ReactNode;
  variant?: SummaryCardVariant;
  subtitle?: React.ReactNode;
  testId?: string;
}> = ({ label, value, variant = "neutral", subtitle, testId }) => {
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border p-4 ${SUMMARY_CARD_VARIANT_STYLES[variant]}`}
    >
      <div className="text-xs font-medium text-neutral-500 uppercase mb-1">
        {label}
      </div>
      <div className="text-xl font-semibold">{value}</div>
      {subtitle && (
        <div className="text-xs text-neutral-500 mt-1">{subtitle}</div>
      )}
    </div>
  );
};

// Proforma churn drill-down content (AC-6) — basis window, immature (< 30 days
// past due) count, and the unpaid units list. Stays informative even at n=0
// (basisCount === 0), showing the window that was checked instead of nothing.
const ChurnBreakdownContent: React.FC<{
  churn: ForecastChurn;
  proformas: ForecastProformaSummary;
  inflow: ForecastInflowResult;
  baseSourceLabel: string;
}> = ({ churn, proformas, inflow, baseSourceLabel }) => {
  const windowLabel = `${formatIsoDatePL(churn.windowFrom)} – ${formatIsoDatePL(churn.windowTo)}`;
  const moreCount = churn.unpaidTotal - churn.unpaid.length;

  return (
    <div className="space-y-2">
      <div className="space-y-0.5 border-b border-neutral-100 pb-2">
        <div className="font-semibold text-neutral-700">Jak liczymy</div>
        <div>
          Bazowy ({baseSourceLabel}): {formatChurnPct(inflow.baseChurnPct)}
        </div>
        {proformas.count === 0 ? (
          <div>Brak otwartych proform w tym miesiącu</div>
        ) : (
          <>
            <div>
              Po terminie w tym miesiącu: {proformas.overdueCount} z{" "}
              {proformas.count} otwartych = {formatChurnPct(inflow.overdueSharePct)}
            </div>
            <div>
              {inflow.isRaised
                ? `Efektywny: ${formatChurnPct(inflow.effectiveProformaChurnPct)} (wyższy z: bazowy ${formatChurnPct(inflow.baseChurnPct)}, po terminie ${formatChurnPct(inflow.overdueSharePct)})`
                : `Efektywny: ${formatChurnPct(inflow.baseChurnPct)} (udział po terminie ${formatChurnPct(inflow.overdueSharePct)} nie przekracza bazowego)`}
            </div>
            {inflow.capBinds && (
              <div>
                Limit kwotowy: z proform wpłynie najwyżej suma nieprzeterminowanych (
                {formatPLN(inflow.proformaCap)})
              </div>
            )}
          </>
        )}
      </div>
      <div>
        Podstawa: <strong>{churn.basisCount}</strong> ({windowLabel})
      </div>
      <div className="text-neutral-500">
        Jednostki &lt; 30 dni po terminie: {churn.immatureCount} (informacyjnie)
      </div>
      {churn.basisCount === 0 ? (
        <p className="text-neutral-500">Brak proform w oknie {windowLabel}.</p>
      ) : (
        <>
          <ul className="divide-y divide-neutral-100">
            {churn.unpaid.map((entry, index) => (
              <li
                key={`${entry.tenantName}-${entry.dueDate}-${index}`}
                className="py-1.5"
              >
                <div className="font-medium text-neutral-900">
                  {entry.tenantName}
                </div>
                <div className="text-neutral-500">
                  {entry.documentNumber ?? "—"} · {formatIsoDatePL(entry.dueDate)} ·{" "}
                  {entry.reason === "canceled" ? "anulowana" : "przeterminowana"}
                </div>
              </li>
            ))}
          </ul>
          {moreCount > 0 && (
            <p className="text-neutral-500">i {moreCount} więcej</p>
          )}
        </>
      )}
    </div>
  );
};

// Churn proform tile (AC-6/AC-7/AC-8/M-13): the % value is the MetricBreakdownPopover
// drill-down trigger; the manual correction lives behind a separate "Koryguj"
// button so the two interactions never share one control (H-6).
// The value is the effective proforma churn (base, raised by the overdue share);
// the drill-down and the correction field show the base.
const ChurnForecastCard: React.FC<{
  churn: ForecastChurn;
  churnOverride: ReturnType<typeof useManualChurnOverride>;
  proformas: ForecastProformaSummary;
  inflow: ForecastInflowResult;
  monthEnded: boolean;
}> = ({ churn, churnOverride, proformas, inflow, monthEnded }) => {
  const { manualPct, isEditing, draft, error, startEditing, handleDraftChange, reset } =
    churnOverride;

  const historicalPct = churn.pct;
  const { isRaised } = inflow;
  const { baseSourceLabel, valueLabel, stateLines, isWarning } = describeChurnState({
    manualPct,
    historicalPct,
    basisCount: churn.basisCount,
    proformaCount: proformas.count,
    overdueCount: proformas.overdueCount,
    inflow,
    monthEnded,
  });
  const variant: SummaryCardVariant = isWarning ? "warning" : "neutral";
  const inputId = "churn-manual-pct-input";
  const errorId = "churn-manual-pct-error";
  const hintId = "churn-manual-pct-hint";
  const stateLabelId = useId();

  return (
    <div
      data-testid="forecast-tile-churn"
      className={`rounded-lg border p-4 ${SUMMARY_CARD_VARIANT_STYLES[variant]}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium text-neutral-500 uppercase mb-1">
          Churn proform
        </div>
        <button
          type="button"
          onClick={startEditing}
          className="text-xs font-medium text-primary-600 hover:text-primary-700 underline shrink-0"
        >
          Koryguj
        </button>
      </div>
      <MetricBreakdownPopover
        title="Churn proform"
        hasContent
        describedById={stateLines.length > 0 ? stateLabelId : undefined}
        content={
          <ChurnBreakdownContent
            churn={churn}
            proformas={proformas}
            inflow={inflow}
            baseSourceLabel={baseSourceLabel}
          />
        }
      >
        <span className="text-xl font-semibold">{valueLabel}</span>
      </MetricBreakdownPopover>
      {stateLines.length > 0 && (
        <div
          id={stateLabelId}
          aria-live="polite"
          className="text-xs text-neutral-500 mt-1"
        >
          {stateLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}
      {isEditing && (
        <div className="mt-3 border-t border-neutral-200 pt-3">
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-neutral-700 mb-1"
          >
            Ręczna korekta (%)
          </label>
          <input
            id={inputId}
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={draft}
            placeholder={
              historicalPct !== null ? formatChurnNumber(historicalPct) : undefined
            }
            onChange={(e) => handleDraftChange(e.target.value)}
            aria-invalid={!!error}
            aria-describedby={error ? `${errorId} ${hintId}` : hintId}
            className={`w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 ${
              error
                ? "border-red-400 focus:ring-red-500"
                : "border-neutral-300 focus:ring-primary-500"
            }`}
          />
          {error && (
            <p id={errorId} className="mt-1 text-xs text-red-600">
              {error}
            </p>
          )}
          <p id={hintId} className="mt-1 text-xs text-neutral-500">
            {isRaised
              ? `Efektywny churn: ${formatChurnPct(inflow.effectiveProformaChurnPct)} (po terminie ${proformas.overdueCount} z ${proformas.count}) — wyższy niż wpisana wartość`
              : "Wartość bazowa — przeterminowane proformy mogą ją podnieść"}
          </p>
          <button
            type="button"
            onClick={reset}
            disabled={manualPct === null}
            className="mt-2 text-xs font-medium text-neutral-600 underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {historicalPct !== null
              ? `Przywróć historyczny (${formatChurnPct(historicalPct)})`
              : "Przywróć historyczny"}
          </button>
        </div>
      )}
    </div>
  );
};

const SubscriptionStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    trialing: "bg-blue-100 text-blue-800",
    past_due: "bg-yellow-100 text-yellow-800",
    canceled: "bg-red-100 text-red-800",
    unpaid: "bg-orange-100 text-orange-800",
    suspended: "bg-neutral-200 text-neutral-700",
    incomplete: "bg-neutral-100 text-neutral-500",
    none: "bg-neutral-100 text-neutral-500",
  };

  const labels: Record<string, string> = {
    active: "Aktywna",
    trialing: "Trial",
    past_due: "Zaległa",
    canceled: "Anulowana",
    unpaid: "Niezapłacona",
    suspended: "Zawieszona",
    incomplete: "Niekompletna",
    none: "Brak",
  };

  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.none}`}
    >
      {labels[status] || status}
    </span>
  );
};

const BillingPeriodBadge: React.FC<{ period: string | null }> = ({ period }) => {
  if (!period) return <span className="text-neutral-400">-</span>;

  const config: Record<string, { label: string; style: string }> = {
    monthly: { label: "Miesięczny", style: "bg-neutral-100 text-neutral-700" },
    quarterly: { label: "Kwartalny", style: "bg-indigo-100 text-indigo-700" },
    yearly: { label: "Roczny", style: "bg-emerald-100 text-emerald-700" },
  };

  const { label, style } = config[period] || { label: period, style: "bg-neutral-100 text-neutral-600" };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {label}
    </span>
  );
};

// Projected Payments Section Component (AC-9)
// Shows both proforma entries (any open proforma with a due date in the month)
// and renewal entries (subscription period ends without one) in a single list.
const ProjectedPaymentsSection: React.FC<{
  payments: ProjectedPayment[];
  proformas: ForecastProformaSummary;
  inflow: ForecastInflowResult;
}> = ({ payments, proformas, inflow }) => {
  const totalAmount = payments.reduce((sum, p) => sum + p.expectedAmount, 0);
  // Overdue proformas are actually excluded only when they raise churn or the
  // not-overdue ceiling binds; otherwise the base churn already covers them.
  const overdueExcluded = inflow.isRaised || inflow.capBinds;
  const overdueBadgeTitle = overdueExcluded
    ? "Nie wlicza się do prognozy"
    : "Po terminie — objęta churnem bazowym";

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
      <h4 className="text-sm font-medium text-purple-800 mb-3">
        Oczekiwane płatności ({payments.length})
      </h4>
      <div className="space-y-2">
        {payments.map((payment) => (
          <div
            key={payment.key}
            className="flex justify-between items-center text-sm bg-white rounded px-3 py-2"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-purple-900">
                {payment.tenantName}
              </span>
              {payment.kind === "proforma" ? (
                <>
                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                    Proforma
                  </span>
                  <span className="text-purple-600">
                    {payment.orderNumber ?? "stary billing"}
                    {payment.documentNumber ? ` · ${payment.documentNumber}` : ""}
                  </span>
                  {payment.dueDate && (
                    <span className="text-purple-600">
                      termin {formatIsoDatePL(payment.dueDate)}
                    </span>
                  )}
                  {payment.overdue && (
                    <span
                      title={overdueBadgeTitle}
                      className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded"
                    >
                      po terminie
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-purple-600">
                    (do {new Date(payment.expectedDate).toLocaleDateString("pl-PL")})
                  </span>
                  {payment.status === "trialing_ending" && (
                    <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                      koniec triala
                    </span>
                  )}
                  {payment.status === "past_due" && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                      zaległa
                    </span>
                  )}
                </>
              )}
            </div>
            <span className="font-mono text-purple-900">
              {formatPLN(payment.expectedAmount)}
            </span>
          </div>
        ))}
      </div>
      <div
        data-testid="projected-payments-summary"
        className="mt-3 pt-3 border-t border-purple-200 space-y-1"
      >
        <div className="flex justify-between items-center text-sm">
          <span className="text-purple-700">Suma oczekiwana</span>
          <span className="font-mono font-semibold text-purple-900">
            {formatPLN(totalAmount)}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-purple-700">Realnie wpłynie</span>
          <span className="font-mono font-semibold text-purple-900">
            {formatPLN(inflow.realisticInflow)}
          </span>
        </div>
        {proformas.overdueCount > 0 && (
          <div className="text-xs text-purple-700">
            {overdueExcluded
              ? `w tym po terminie (wyłączone z prognozy): ${proformas.overdueCount} (${formatPLN(proformas.overdueTotal)})`
              : `w tym po terminie: ${proformas.overdueCount} (${formatPLN(proformas.overdueTotal)}) — objęte churnem bazowym, nie zmieniają prognozy`}
          </div>
        )}
      </div>
    </div>
  );
};

const OperationTypeBadge: React.FC<{ type: SettlementOperationType }> = ({
  type,
}) => {
  const config: Record<string, { label: string; style: string }> = {
    stripe_payment: {
      label: "Płatność Stripe",
      style: "bg-green-100 text-green-800",
    },
    bank_transfer_payment: {
      label: "Przelew bankowy",
      style: "bg-emerald-100 text-emerald-800",
    },
    fixed_cost: { label: "Koszt stały", style: "bg-red-100 text-red-800" },
    payout_owner: {
      label: "Wypłata Michał",
      style: "bg-blue-100 text-blue-800",
    },
    payout_partner: {
      label: "Wypłata Paulina",
      style: "bg-purple-100 text-purple-800",
    },
  };

  const { label, style } = config[type] || {
    label: type,
    style: "bg-neutral-100 text-neutral-600",
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {label}
    </span>
  );
};

// Add Operation Modal
const AddOperationModal: React.FC<{
  month: string;
  onClose: () => void;
}> = ({ month, onClose }) => {
  const createMutation = useCreateOperationMutation();
  const [formData, setFormData] = useState<CreateOperationDto>({
    type: "fixed_cost",
    amount: 30000, // default 300 PLN
    month,
    description: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Dodaj operacje
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Typ operacji
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  type: e.target.value as CreateOperationDto["type"],
                })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              <option value="fixed_cost">Koszt stały</option>
              <option value="payout_owner">Wypłata Michał</option>
              <option value="payout_partner">Wypłata Paulina</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Kwota netto (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount / 100}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  amount: Math.round(parseFloat(e.target.value || "0") * 100),
                })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Opis (opcjonalnie)
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="np. Serwer, domena, OpenAI"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || formData.amount <= 0}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-4 py-2 rounded-md"
            >
              {createMutation.isPending ? "Dodawanie..." : "Dodaj"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BankTransferPaymentModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { data: customers } = useKiedyNaboryCustomersQuery();
  const registerPaymentMutation = useRegisterBankTransferPaymentMutation();

  const [formData, setFormData] = useState<{
    tenantId: string;
    amount: string;
    paymentDate: string;
    referenceNumber: string;
    billingPeriod: BillingPeriod;
    extendSubscription: boolean;
    notes: string;
  }>({
    tenantId: "",
    amount: "299.00",
    paymentDate: toDateInputValue(new Date().toISOString()),
    referenceNumber: "",
    billingPeriod: "month",
    extendSubscription: true,
    notes: "",
  });

  useEffect(() => {
    if (formData.tenantId || !customers?.length) return;
    setFormData((prev) => ({ ...prev, tenantId: customers[0].tenantId }));
  }, [customers, formData.tenantId]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.tenantId) return;

    const amount = Math.round(parseFloat(formData.amount || "0") * 100);
    if (!amount || amount < 100) return;

    registerPaymentMutation.mutate(
      {
        tenantId: formData.tenantId,
        amount,
        paymentDate: toIsoStartOfDay(formData.paymentDate),
        referenceNumber: formData.referenceNumber || undefined,
        billingPeriod: formData.billingPeriod,
        extendSubscription: formData.extendSubscription,
        notes: formData.notes || undefined,
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-xl w-full mx-4 shadow-xl">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Dodaj płatność przelewową
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Klient
            </label>
            <select
              value={formData.tenantId}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  tenantId: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              <option value="">Wybierz klienta</option>
              {customers?.map((customer) => (
                <option key={customer.tenantId} value={customer.tenantId}>
                  {customer.tenantName}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Kwota netto (PLN)
              </label>
              <input
                type="number"
                step="0.01"
                min="1"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Data wpłaty
              </label>
              <input
                type="date"
                value={formData.paymentDate}
                max={toDateInputValue(new Date().toISOString())}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    paymentDate: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Numer referencyjny (opcjonalnie)
            </label>
            <input
              type="text"
              value={formData.referenceNumber}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  referenceNumber: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Okres rozliczeniowy
            </label>
            <div className="flex gap-4 text-sm">
              {(["month", "quarter", "year"] as BillingPeriod[]).map((period) => (
                <label key={period} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="billingPeriod"
                    value={period}
                    checked={formData.billingPeriod === period}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        billingPeriod: period,
                      }))
                    }
                  />
                  {period === "month" && "Miesiąc"}
                  {period === "quarter" && "Kwartał"}
                  {period === "year" && "Rok"}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="extendSubscription"
              type="checkbox"
              checked={formData.extendSubscription}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  extendSubscription: e.target.checked,
                }))
              }
              disabled={registerPaymentMutation.isPending}
              className="w-4 h-4"
            />
            <label htmlFor="extendSubscription" className="text-sm text-neutral-700">
              Automatycznie przedłuż subskrypcję
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notatki
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={
                registerPaymentMutation.isPending ||
                !formData.tenantId ||
                parseFloat(formData.amount || "0") < 1
              }
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white px-4 py-2 rounded-md"
            >
              {registerPaymentMutation.isPending ? "Dodawanie..." : "Dodaj płatność"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Billing Operation Modal - Manual Billing Flow
const BillingOperationModal: React.FC<{
  tenantId: string;
  customer: KiedyNaboryCustomer;
  currentPeriodEnd?: string | null;
  onClose: () => void;
}> = ({ tenantId, customer, currentPeriodEnd, onClose }) => {
  const createMutation = useCreateBillingOperationMutation(tenantId);

  const [form, setForm] = useState<{
    paymentRecorded: boolean;
    paymentDate: string;
    paymentMethod: BillingOperationPaymentMethod;
    amountNet: string;
    vatRate: number;
    extendSubscription: boolean;
    subscriptionPeriodEnd: string;
    issueInvoice: boolean;
    billingPeriodStart: string;
    billingPeriodEnd: string;
    sendEmail: boolean;
    reason: string;
    notes: string;
  }>({
    paymentRecorded: true,
    paymentDate: toDateInputValue(new Date().toISOString()),
    paymentMethod: "bank_transfer",
    amountNet: "99.00",
    vatRate: 0.23,
    extendSubscription: true,
    subscriptionPeriodEnd: toDateInputValue(
      addMonths(currentPeriodEnd ? new Date(currentPeriodEnd) : new Date(), 1).toISOString()
    ),
    issueInvoice: true,
    billingPeriodStart: toDateInputValue(
      currentPeriodEnd ? new Date(currentPeriodEnd).toISOString() : new Date().toISOString()
    ),
    billingPeriodEnd: toDateInputValue(
      addMonths(currentPeriodEnd ? new Date(currentPeriodEnd) : new Date(), 1).toISOString()
    ),
    sendEmail: true,
    reason: "",
    notes: "",
  });

  // Calculate gross amount from net
  const amountNetCents = Math.round(parseFloat(form.amountNet || "0") * 100);
  const amountGrossCents = Math.round(amountNetCents * (1 + form.vatRate));
  const amountGross = (amountGrossCents / 100).toFixed(2);

  const canSubmit =
    form.reason.trim().length >= 10 &&
    (!form.paymentRecorded || (amountNetCents > 0 && form.paymentDate)) &&
    (!form.extendSubscription || form.subscriptionPeriodEnd) &&
    (!form.issueInvoice || (form.billingPeriodStart && form.billingPeriodEnd));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    const dto: CreateBillingOperationDto = {
      paymentRecorded: form.paymentRecorded,
      extendSubscription: form.extendSubscription,
      issueInvoice: form.issueInvoice,
      sendEmail: form.sendEmail,
      reason: form.reason.trim(),
      notes: form.notes || undefined,
    };

    if (form.paymentRecorded) {
      dto.paymentDate = toIsoStartOfDay(form.paymentDate);
      dto.paymentMethod = form.paymentMethod;
      dto.amountNetCents = amountNetCents;
      dto.amountGrossCents = amountGrossCents;
      dto.vatRate = form.vatRate;
    }

    if (form.extendSubscription) {
      dto.subscriptionPeriodEnd = toIsoEndOfDay(form.subscriptionPeriodEnd);
    }

    if (form.issueInvoice) {
      dto.billingPeriodStart = form.billingPeriodStart;
      dto.billingPeriodEnd = form.billingPeriodEnd;
    }

    createMutation.mutate(dto, {
      onSuccess: () => onClose(),
    });
  };

  const applyQuickPeriod = (months: number) => {
    const baseDate = currentPeriodEnd ? new Date(currentPeriodEnd) : new Date();
    const newPeriodEnd = addMonths(baseDate, months);
    setForm((prev) => ({
      ...prev,
      subscriptionPeriodEnd: toDateInputValue(newPeriodEnd.toISOString()),
      billingPeriodStart: toDateInputValue(baseDate.toISOString()),
      billingPeriodEnd: toDateInputValue(newPeriodEnd.toISOString()),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex justify-between items-center sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Nowa operacja billingowa
            </h3>
            <p className="text-sm text-neutral-500">
              {customer.tenantName}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Zamknij modal"
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Payment Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="paymentRecorded"
                checked={form.paymentRecorded}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, paymentRecorded: e.target.checked }))
                }
                disabled={createMutation.isPending}
                className="w-4 h-4"
              />
              <label htmlFor="paymentRecorded" className="font-medium text-neutral-900">
                Zarejestruj płatność
              </label>
            </div>

            {form.paymentRecorded && (
              <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-neutral-50 rounded-md">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Kwota netto (PLN)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.amountNet}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, amountNet: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Brutto (z VAT {(form.vatRate * 100).toFixed(0)}%): {amountGross} PLN
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Data płatności
                  </label>
                  <input
                    type="date"
                    value={form.paymentDate}
                    max={toDateInputValue(new Date().toISOString())}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, paymentDate: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Metoda płatności
                  </label>
                  <select
                    value={form.paymentMethod}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        paymentMethod: e.target.value as BillingOperationPaymentMethod,
                      }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  >
                    <option value="bank_transfer">Przelew bankowy</option>
                    <option value="manual">Ręczna</option>
                    <option value="other">Inna</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Stawka VAT
                  </label>
                  <select
                    value={form.vatRate}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, vatRate: parseFloat(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  >
                    <option value="0.23">23%</option>
                    <option value="0.08">8%</option>
                    <option value="0">0%</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Extension Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="extendSubscription"
                checked={form.extendSubscription}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, extendSubscription: e.target.checked }))
                }
                disabled={createMutation.isPending}
                className="w-4 h-4"
              />
              <label htmlFor="extendSubscription" className="font-medium text-neutral-900">
                Przedłuż subskrypcję
              </label>
            </div>

            {form.extendSubscription && (
              <div className="ml-7 p-4 bg-neutral-50 rounded-md">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nowa data ważności
                </label>
                <input
                  type="date"
                  value={form.subscriptionPeriodEnd}
                  min={toDateInputValue(new Date().toISOString())}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, subscriptionPeriodEnd: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => applyQuickPeriod(1)}
                    className="px-2 py-1 text-xs border border-neutral-200 rounded-md hover:bg-neutral-100"
                  >
                    +1 mies.
                  </button>
                  <button
                    type="button"
                    onClick={() => applyQuickPeriod(3)}
                    className="px-2 py-1 text-xs border border-neutral-200 rounded-md hover:bg-neutral-100"
                  >
                    +3 mies.
                  </button>
                  <button
                    type="button"
                    onClick={() => applyQuickPeriod(12)}
                    className="px-2 py-1 text-xs border border-neutral-200 rounded-md hover:bg-neutral-100"
                  >
                    +12 mies.
                  </button>
                </div>
                {currentPeriodEnd && (
                  <p className="text-xs text-neutral-500 mt-2">
                    Aktualna data ważności: {formatDate(currentPeriodEnd)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Invoice Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="issueInvoice"
                checked={form.issueInvoice}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, issueInvoice: e.target.checked }))
                }
                disabled={createMutation.isPending}
                className="w-4 h-4"
              />
              <label htmlFor="issueInvoice" className="font-medium text-neutral-900">
                Wystaw fakturę VAT
              </label>
            </div>

            {form.issueInvoice && (
              <div className="ml-7 p-4 bg-neutral-50 rounded-md grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Okres rozliczeniowy od
                  </label>
                  <input
                    type="date"
                    value={form.billingPeriodStart}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, billingPeriodStart: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Okres rozliczeniowy do
                  </label>
                  <input
                    type="date"
                    value={form.billingPeriodEnd}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, billingPeriodEnd: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                  />
                </div>
                {!customer.nip && (
                  <div className="col-span-2 text-amber-600 text-sm bg-amber-50 p-2 rounded">
                    Uwaga: Klient nie ma ustawionego NIP. Faktura może nie zostać wystawiona poprawnie.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Email Section */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="sendEmail"
              checked={form.sendEmail}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, sendEmail: e.target.checked }))
              }
              disabled={createMutation.isPending}
              className="w-4 h-4"
            />
            <label htmlFor="sendEmail" className="font-medium text-neutral-900">
              Wyślij email do klienta
            </label>
            {form.sendEmail && form.issueInvoice && (
              <span className="text-xs text-neutral-500">(z fakturą)</span>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Powód operacji (min. 10 znaków) <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
              rows={2}
              placeholder="np. Otrzymano przelew za styczeń 2026"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notatki (opcjonalnie)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          {/* Summary */}
          <div className="bg-primary-50 border border-primary-200 rounded-md p-4 text-sm">
            <div className="font-medium text-primary-900 mb-2">Podsumowanie operacji:</div>
            <ul className="space-y-1 text-primary-800">
              {form.paymentRecorded && (
                <li>Płatność: {amountGross} PLN brutto ({form.amountNet} PLN netto)</li>
              )}
              {form.extendSubscription && (
                <li>Przedłużenie do: {formatDate(form.subscriptionPeriodEnd)}</li>
              )}
              {form.issueInvoice && (
                <li>Faktura VAT za okres: {formatDate(form.billingPeriodStart)} - {formatDate(form.billingPeriodEnd)}</li>
              )}
              {form.sendEmail && (
                <li>Email {form.issueInvoice ? "z fakturą" : "z potwierdzeniem"}</li>
              )}
              {!form.paymentRecorded && !form.extendSubscription && !form.issueInvoice && (
                <li className="text-amber-600">Brak wybranych akcji</li>
              )}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={!canSubmit || createMutation.isPending}
              className="bg-primary-700 hover:bg-primary-800 disabled:bg-primary-300 text-white px-4 py-2 rounded-md"
            >
              {createMutation.isPending ? "Zapisywanie..." : "Zapisz operację"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =====================================================
// Proforma Modal - Admin Proforma Generator
// =====================================================

interface ProformaModalProps {
  customer: KiedyNaboryCustomer;
  onClose: () => void;
}

/**
 * Modal for creating proforma invoices
 *
 * @see docs/specifications/2026-02-18-admin-proforma-generator.md UI Specifications
 */
const ProformaModal: React.FC<ProformaModalProps> = ({
  customer,
  onClose,
}) => {
  const tenantId = customer.tenantId;
  const createProformaMutation = useCreateAdminProformaMutation(tenantId);
  const detailsQuery = useAdminSubscriptionDetailsQuery(tenantId);
  const { data: plans } = useSubscriptionPlans();
  const firstRadioButtonRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Get plan name from subscription details
  const planName = detailsQuery.data?.subscription?.planName;

  // Find the plan for this customer to get prices
  const customerPlan = plans?.find((p) => p.name === planName);

  // Get prices for each billing interval
  const getPriceForInterval = (interval: string): SubscriptionPrice | undefined => {
    return customerPlan?.prices?.find(
      (p) => p.billingInterval === interval && p.isActive
    );
  };

  const monthPrice = getPriceForInterval("month");
  const quarterPrice = getPriceForInterval("quarter");
  const yearPrice = getPriceForInterval("year");

  // Form state - auto-select first available period
  const [billingPeriod, setBillingPeriod] = useState<AdminProformaBillingInterval>(() => {
    if (monthPrice) return "month";
    if (quarterPrice) return "quarter";
    if (yearPrice) return "year";
    return "month";
  });
  const [sendEmail, setSendEmail] = useState(true);

  // Get selected price info
  const selectedPrice =
    billingPeriod === "month"
      ? monthPrice
      : billingPeriod === "quarter"
        ? quarterPrice
        : yearPrice;

  const hasPrice = !!selectedPrice;

  // Focus management: focus first radio button on mount
  useEffect(() => {
    firstRadioButtonRef.current?.focus();
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasPrice || createProformaMutation.isPending) return;

    createProformaMutation.mutate(
      { billingPeriod, sendEmail },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const isLoading = createProformaMutation.isPending || detailsQuery.isLoading;
  const errorMessage =
    (createProformaMutation.error as any)?.response?.data?.message ||
    (createProformaMutation.error ? "Wystapil blad podczas wystawiania proformy" : null);

  // Check if customer has no subscription (404 from details query)
  const hasNoSubscription = isNotFoundError(detailsQuery.error);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="proforma-modal-title"
      aria-busy={isLoading}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2
            id="proforma-modal-title"
            className="text-lg font-semibold text-neutral-900"
          >
            Wystaw fakture proforma
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Zamknij modal"
            className="text-neutral-400 hover:text-neutral-700"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* Loading State */}
        {detailsQuery.isLoading && (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}

        {/* No Subscription Error */}
        {hasNoSubscription && (
          <div className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex gap-3">
                <span className="text-amber-500 text-lg">⚠️</span>
                <div>
                  <p className="font-medium text-amber-800">
                    Brak aktywnej subskrypcji
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Klient nie ma aktywnej subskrypcji. Najpierw utworz subskrypcje
                    w panelu szczegolow klienta.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
              >
                Zamknij
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {!detailsQuery.isLoading && !hasNoSubscription && (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer Info */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Klient:</span>
              <span className="font-medium text-neutral-900">{customer.tenantName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Plan:</span>
              <span className="font-medium text-neutral-900">{planName || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Wazne do:</span>
              <span className="font-medium text-neutral-900">
                {customer.currentPeriodEnd
                  ? formatDate(customer.currentPeriodEnd)
                  : "-"}
              </span>
            </div>
          </div>

          {/* Stripe Payment Source Warning */}
          {detailsQuery.data?.subscription?.paymentSource === "stripe" && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex gap-3">
                <span className="text-amber-500 text-lg">⚠️</span>
                <div>
                  <p className="font-medium text-amber-800">
                    Zmiana metody platnosci
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Wystawienie proformy zmieni metode platnosci z karty płatniczej na przelew bankowy.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error State - No Price */}
          {!hasPrice && !isLoading && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex gap-3">
                <span className="text-amber-500 text-lg">⚠️</span>
                <div>
                  <p className="font-medium text-amber-800">
                    Brak ceny dla wybranego planu i okresu
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Plan "{planName || 'nieznany'}" nie ma zdefiniowanej ceny dla
                    wybranego okresu. Wybierz inny okres lub skontaktuj sie z
                    administratorem.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* API Error State */}
          {errorMessage && !isLoading && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex gap-3">
                <span className="text-red-500 text-lg">❌</span>
                <div>
                  <p className="font-medium text-red-800">Blad wystawiania proformy</p>
                  <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Billing Period Radio Group */}
          <fieldset
            role="radiogroup"
            aria-labelledby="period-label"
            aria-describedby="period-desc"
          >
            <legend id="period-label" className="text-sm font-medium text-neutral-700 mb-3">
              Okres rozliczeniowy
            </legend>
            <span id="period-desc" className="sr-only">
              Wybierz jeden z trzech okresow rozliczeniowych
            </span>
            <div className="space-y-2">
              {/* Month */}
              <label
                className={`flex items-center justify-between p-3 border rounded-md cursor-pointer ${
                  billingPeriod === "month"
                    ? "border-primary-500 bg-primary-50"
                    : "border-neutral-200 hover:border-neutral-300"
                } ${!monthPrice ? "opacity-50" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <input
                    ref={firstRadioButtonRef}
                    type="radio"
                    name="billingPeriod"
                    value="month"
                    checked={billingPeriod === "month"}
                    onChange={() => setBillingPeriod("month")}
                    disabled={isLoading || !monthPrice}
                    aria-label={`Miesiac, ${monthPrice ? formatPLN(monthPrice.amount) : 'brak ceny'}`}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="font-medium text-neutral-900">Miesiac</span>
                </div>
                <span className="text-sm text-neutral-600">
                  {monthPrice ? `${formatPLN(monthPrice.amount)} netto` : "brak ceny"}
                </span>
              </label>

              {/* Quarter */}
              <label
                className={`flex items-center justify-between p-3 border rounded-md cursor-pointer ${
                  billingPeriod === "quarter"
                    ? "border-primary-500 bg-primary-50"
                    : "border-neutral-200 hover:border-neutral-300"
                } ${!quarterPrice ? "opacity-50" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="billingPeriod"
                    value="quarter"
                    checked={billingPeriod === "quarter"}
                    onChange={() => setBillingPeriod("quarter")}
                    disabled={isLoading || !quarterPrice}
                    aria-label={`Kwartal, ${quarterPrice ? formatPLN(quarterPrice.amount) : 'brak ceny'}`}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="font-medium text-neutral-900">Kwartal</span>
                </div>
                <span className="text-sm text-neutral-600">
                  {quarterPrice ? `${formatPLN(quarterPrice.amount)} netto` : "brak ceny"}
                </span>
              </label>

              {/* Year */}
              <label
                className={`flex items-center justify-between p-3 border rounded-md cursor-pointer ${
                  billingPeriod === "year"
                    ? "border-primary-500 bg-primary-50"
                    : "border-neutral-200 hover:border-neutral-300"
                } ${!yearPrice ? "opacity-50" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="billingPeriod"
                    value="year"
                    checked={billingPeriod === "year"}
                    onChange={() => setBillingPeriod("year")}
                    disabled={isLoading || !yearPrice}
                    aria-label={`Rok, ${yearPrice ? formatPLN(yearPrice.amount) : 'brak ceny'}`}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="font-medium text-neutral-900">Rok</span>
                </div>
                <span className="text-sm text-neutral-600">
                  {yearPrice ? `${formatPLN(yearPrice.amount)} netto` : "brak ceny"}
                </span>
              </label>
            </div>
          </fieldset>

          {/* Summary */}
          {hasPrice && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Kwota netto:</span>
                  <span className="font-mono font-medium text-neutral-900">
                    {formatPLN(selectedPrice.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Kwota brutto (23% VAT):</span>
                  <span className="font-mono font-medium text-neutral-900">
                    {formatPLN(Math.round(selectedPrice.amount * 1.23))}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Send Email Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="sendProformaEmail"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="sendProformaEmail" className="text-sm text-neutral-700">
              Wyslij email do klienta z proforma
            </label>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-4 text-neutral-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-3" />
              Wystawianie proformy...
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={!hasPrice || isLoading}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-4 py-2 rounded-md"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Wystawianie...
                </span>
              ) : (
                "Wystaw i wyslij"
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default KiedyNaborySettlements;
